import { useMemo } from 'react';
import { wines, categories, foods, regions } from '../data/wines';
import { filterWinesByFood } from '../utils/foodPairingUtils';
import {
  getVisibleWines,
  getUniqueGrapes,
  applyWineListFilters,
  getWineListPageTitle,
} from '../utils/wineHelpers';

const getListCategoryFlags = (category, selectedFood, selectedRegion) => {
  const noContext = !selectedFood && !selectedRegion;
  return {
    isGrapeCategory: category === 'grape' && noContext,
    isPriceCategory: category === 'price' && noContext,
  };
};

const filterWinesByContext = (allWines, visibleWines, selectedFood, selectedRegion, foodId) => {
  if (selectedFood) {
    return filterWinesByFood(allWines, foodId);
  }
  if (selectedRegion) {
    return visibleWines.filter((wine) => wine.region === selectedRegion.name);
  }
  return visibleWines;
};

export const useWineListData = ({
  category,
  foodId,
  regionId,
  classificationFilter,
  grapeFilter,
  priceRange,
  sortOrder,
  priceRanges,
}) => {
  const selectedFood = foodId ? foods.find((f) => f.id === foodId) : null;
  const selectedRegion = regionId ? regions.find((r) => r.id === regionId) : null;
  const visibleWines = useMemo(() => getVisibleWines(wines), []);

  const contextFilteredWines = useMemo(
    () => filterWinesByContext(wines, visibleWines, selectedFood, selectedRegion, foodId),
    [selectedFood, selectedRegion, foodId, visibleWines]
  );

  const availableGrapes = useMemo(
    () => getUniqueGrapes(contextFilteredWines),
    [contextFilteredWines]
  );

  const filteredWines = useMemo(
    () => applyWineListFilters(
      contextFilteredWines,
      {
        classification: classificationFilter,
        grape: grapeFilter,
        priceRangeId: priceRange,
        sortOrder,
      },
      priceRanges
    ),
    [contextFilteredWines, classificationFilter, grapeFilter, priceRange, sortOrder, priceRanges]
  );

  const pageTitle = getWineListPageTitle({ selectedFood, selectedRegion, category, categories });
  const { isGrapeCategory, isPriceCategory } = getListCategoryFlags(category, selectedFood, selectedRegion);
  const hasResultCount = Boolean(selectedFood || selectedRegion || isGrapeCategory || isPriceCategory);

  return {
    selectedFood,
    selectedRegion,
    availableGrapes,
    filteredWines,
    pageTitle,
    isGrapeCategory,
    isPriceCategory,
    resultCount: hasResultCount ? filteredWines.length : null,
  };
};

export const getWineListBackPath = (selectedFood, selectedRegion) => {
  if (selectedFood) {
    return '/foods';
  }
  if (selectedRegion) {
    return '/regions';
  }
  return '/';
};
